import { cloneElement } from "react";
import { Field as ChakraField, Input, Textarea, Switch, Select, Text, Box, createListCollection } from "@chakra-ui/react";
import { useTranslation } from "../i18nShim";
import { getIn } from "formik";

export const isFieldInvalid = (formik, path) => Boolean(getIn(formik.errors, path));

/** True when a required field is still incomplete on a step the user has already left. */
export const isFieldWarning = (warningErrors, visited, path) => visited && Boolean(getIn(warningErrors, path));

const stateBorderColor = (invalid, warning) => (invalid ? "red.500" : warning ? "yellow.500" : undefined);

export const Field = ({ label, required, invalid, warning, hint, children }) => {
    const borderColor = stateBorderColor(invalid, warning);
    return (
        <ChakraField.Root>
            <ChakraField.Label fontSize="sm" fontWeight={500} color={invalid ? "red.400" : "text.white"}>
                {label}
                {required && <Text as="span" color={invalid ? "red.400" : "purple.400"} fontWeight={700}> *</Text>}
            </ChakraField.Label>
            {borderColor ? cloneElement(children, { borderColor }) : children}
            {hint && <ChakraField.HelperText fontSize="xs" color="text.grey">{hint}</ChakraField.HelperText>}
        </ChakraField.Root>
    );
};

export const FormInput = (props) => (
    <Input
        bg="bg.dark"
        borderColor="border.primary"
        color="white"
        fontSize="sm"
        transition="border-color 0.15s, box-shadow 0.15s"
        _placeholder={{ color: "gray.600" }}
        _hover={{ borderColor: "border.secondary" }}
        _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)" }}
        {...props}
    />
);

export const FormTextarea = ({ rows = 3, ...props }) => (
    <Textarea
        rows={rows}
        bg="bg.dark"
        borderColor="border.primary"
        color="white"
        fontSize="sm"
        resize="none"
        transition="border-color 0.15s, box-shadow 0.15s"
        _placeholder={{ color: "gray.600" }}
        _hover={{ borderColor: "border.secondary" }}
        _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)" }}
        {...props}
    />
);

export const AdditionalInfoField = ({ value, onChange, rows = 2, ...props }) => {
    const { t } = useTranslation("jobOffer");
    return (
        <Field label={t("additionalInfo.label")}>
            <FormTextarea
                value={value ?? ""}
                onChange={onChange}
                placeholder={t("additionalInfo.placeholder")}
                rows={rows}
                {...props}
            />
        </Field>
    );
};

export const FormSelect = ({ value, onChange, options, placeholder, multiple = false, borderColor = "border.primary" }) => {
    const collection = createListCollection({ items: options });
    const selected = value ? (Array.isArray(value) ? value : [value]) : [];
    const isEmpty = selected.length === 0;
    return (
        <Select.Root
            collection={collection}
            value={selected}
            multiple={multiple}
            onValueChange={(e) => onChange(multiple ? e.value : e.value[0] ?? "")}
        >
            <Select.HiddenSelect />
            <Select.Control>
                <Select.Trigger
                    bg="bg.dark"
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="l2"
                    h="10"
                    fontSize="sm"
                    transition="border-color 0.15s, box-shadow 0.15s"
                    _hover={{ borderColor: "border.secondary" }}
                    _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)" }}
                >
                    <Select.ValueText placeholder={placeholder} color={isEmpty ? "gray.600" : "white"} />
                </Select.Trigger>
                <Select.IndicatorGroup>
                    <Select.Indicator />
                </Select.IndicatorGroup>
            </Select.Control>
            <Select.Positioner>
                <Select.Content bg="bg.dark" borderColor="border.primary">
                    {options.map((opt) => (
                        <Select.Item item={opt} key={opt.value} color="white">
                            {opt.label}
                            <Select.ItemIndicator />
                        </Select.Item>
                    ))}
                </Select.Content>
            </Select.Positioner>
        </Select.Root>
    );
};

export const FormSwitch = ({ label, checked, onChange, description }) => (
    <Switch.Root w="full" checked={checked} onCheckedChange={(e) => onChange(e.checked)}>
        <Switch.HiddenInput />
        <Switch.Control flexShrink={0} bg={checked ? "purple.500" : "bg.gray"}>
            <Switch.Thumb />
        </Switch.Control>
        <Switch.Label flex={1} minW={0}>
            <Text fontSize="sm" color="text.white" wordBreak="normal" overflowWrap="break-word">{label}</Text>
            {description && <Text fontSize="xs" color="text.grey" wordBreak="normal" overflowWrap="break-word">{description}</Text>}
        </Switch.Label>
    </Switch.Root>
);

export const FormCheckbox = ({ label, checked, onChange }) => (
    <Box as="label" display="flex" alignItems="center" gap={2} cursor="pointer">
        <Box
            as="input"
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            w={4}
            h={4}
            accentColor="purple.500"
            cursor="pointer"
            flexShrink={0}
        />
        <Text fontSize="sm" color="text.white">{label}</Text>
    </Box>
);

export const WeightField = ({ value, onChange, label }) => {
    const { t } = useTranslation("jobOffer");
    return (
    <Field label={label ?? t("scoringPanel.weight")} hint={t("scoringPanel.weightHint")}>
        <FormInput
            type="number"
            min="0"
            max="10"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        />
    </Field>
    );
};
